export const VAULT_PARAMS_PERMISSION_RESOURCE = 'vault:params';
/** The `resource` the BFF writes vault parameter saves under in audit_logs
 * (@AuditLogWithRecord, record id `core` / `risk`). Same string as the Casbin
 * resource today, but a different contract: this one names audit rows. */
export const VAULT_PARAMS_AUDIT_RESOURCE = 'vault:params';

export const VAULT_PARAMS_NS = 'module.vaultParams';

/** Mirrors the engine's rules (design spec §3). The engine stays the authority. */
export const LOCKUP_DAYS_MIN = 1;
export const LOCKUP_DAYS_MAX = 7;
export const PERFORMANCE_FEE_CAP_MAX_PERCENT = 30;
/** Live vaults one manager may hold (XBIT-4659): 1–100. */
export const MAX_LIVE_VAULTS_PER_MANAGER_MIN = 1;
export const MAX_LIVE_VAULTS_PER_MANAGER_MAX = 100;
export const MAX_LEVERAGE_CEILING = 50;
/** Redemption close timeout upper bound (M1): 1–86400 s (24h). */
export const CLOSE_TIMEOUT_SECONDS_MAX = 86400;
/** Venue-clock upper bounds (ms), engine `runtimecfg` *MaxMs. Each is > 0; the read
 * span has no bound of its own, only span + lag allowance ≤ skew band. */
export const EVENT_TIME_TOLERANCE_MS_MAX = 5000;
export const VENUE_LAG_ALLOWANCE_MS_MAX = 5000;
export const VENUE_TIME_SKEW_BAND_MS_MAX = 10000;
/** Fixed6 on the wire: 6 dp as a fraction is 4 dp as a percent. */
export const PERCENT_MAX_DECIMALS = 4;
export const DECIMAL_MAX_DECIMALS = 6;
/** An exact Hyperliquid perp coin name, with the optional builder-deployed dex
 * prefix of a HIP-3 coin (`xyz:GOLD`, XBIT-4794). Case is significant (kPEPE);
 * a dex is lower-case letters and digits. The engine's `hlPerpCoin` is the same
 * pattern (`runtimecfg/vault_params.go`); keep the two in step. */
export const HL_PERP_COIN = /^([a-z0-9]{1,20}:)?[A-Za-z0-9]{1,20}$/;
/** Hyperliquid's 1000× names: a lower-case `k` then the upper-case base (kPEPE,
 * kBONK). The only mixed-case family on the venue, so the only one the console
 * keeps as typed; every other entry is recorded in upper case (QA 2026-09-29). */
export const HL_K_COIN = /^k[A-Z0-9]{1,19}$/;

/**
 * `body.code` values the BFF returns for `/vault/params` (C-06 error mapping).
 * The BFF and this app deploy independently, so the pre-split code is kept:
 * an older BFF answers every upstream failure with 422 `VAULT_ENGINE_UPSTREAM_ERROR`.
 * Kept here, not in the service, so helpers stay free of the HTTP client.
 */
export const VAULT_PARAMS_ERROR = {
  /** 409 — the key moved since it was read (CAS). */
  CONFLICT: 'VAULT_PARAMS_CONFLICT',
  /** 422 — the engine rejected the value; `message` is the engine's reason. */
  INVALID: 'VAULT_PARAMS_INVALID',
  /** 502 — the engine refused the admin service's credentials. */
  ENGINE_AUTH_FAILED: 'VAULT_ENGINE_AUTH_FAILED',
  /** 503 — the engine or its store is unreachable. */
  ENGINE_UNAVAILABLE: 'VAULT_ENGINE_UNAVAILABLE',
  /** 504 — the engine call timed out; a write may still have been applied. */
  ENGINE_TIMEOUT: 'VAULT_ENGINE_TIMEOUT',
  /** 503 — the BFF has no engine URL for this stage. */
  ENGINE_NOT_CONFIGURED: 'VAULT_ENGINE_NOT_CONFIGURED',
  /** 422 — older BFF only: conflict and rejected value share this code. */
  ENGINE_UPSTREAM_ERROR: 'VAULT_ENGINE_UPSTREAM_ERROR',
} as const;
